import { FC, useEffect, useRef } from "react";
import { motion } from "framer-motion";

interface MatrixBackgroundProps {
  isDark?: boolean;
}

const MatrixBackground: FC<MatrixBackgroundProps> = ({ isDark = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.style.width = "100vw";
    canvas.height = window.innerHeight;
    canvas.style.height = "100vh";

    const characters = "01";
    const fontSize = 14;
    const columns = canvas.width / fontSize;
    const drops: number[] = [];

    for (let i = 0; i < columns; i++) {
      drops[i] = 1;
    }

    const draw = () => {
      // Background fill color based on theme
      ctx.fillStyle = isDark 
        ? "rgba(0, 0, 0, 0.05)" 
        : "rgba(250, 250, 250, 0.05)";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Text color based on theme
      ctx.fillStyle = isDark 
        ? "rgba(255, 255, 255, 0.1)" 
        : "rgba(0, 0, 0, 0.1)";
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < drops.length; i++) {
        const text = characters[Math.floor(Math.random() * characters.length)];
        ctx.fillText(text, i * fontSize, drops[i] * fontSize);

        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0;
        }

        drops[i]++;
      }
    };

    const interval = setInterval(draw, 50);

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, [isDark]);

  return (
    <motion.canvas
      ref={canvasRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
      className="fixed inset-0 w-screen h-screen pointer-events-none"
    />
  );
};

export default MatrixBackground;
